// THE DOOR — lobby, then the wait, then the table.
//
// Page.tsx is deliberately NOT MOUNTED until a table has been chosen. That is
// what makes the lobby silent: the audio context, the round loop, the wheel's
// rAF and the fake-chip emitter all live inside Page and none of them exist
// until this component decides they should. It is also why the first tap in the
// lobby is worth having — it is a real user gesture, which is the only context
// iOS will unlock speech from.
//
// Keeping the phases in a wrapper rather than inside Page is the whole reason
// this is cheap: Page did not have to learn about any of it, and it still owns
// exactly what it owned before.
//
// ── TWO LOBBIES BEHIND ONE DOOR (Phase E, plan §5.7, 2026-09-06) ─────────────
// `GET /roulette/mode` answers which topology the server is running, and this
// component asks once, here, before anything else exists. There are two reasons
// the question belongs at the door rather than inside Page:
//
//   1. The LOBBY is the first thing that differs. With the flag off it is the
//      nine derived virtual tables of `tables.ts`; with it on it is the real
//      rows of `shared_tables`. Deciding after a table has been picked would
//      mean picking from the wrong list.
//   2. None of the shared path RUNS while the flag is off, and none of it is in
//      the app's main bundle. The clock is behind a dynamic `import()` (vite
//      emits it as its own chunk), so a public game that has been live since
//      1 September gains nothing on its boot path; the chunk is fetched when
//      roulette is opened, purely to ask the question, and the clock inside it
//      is never CONSTRUCTED unless the answer is yes. Nothing polls, nothing
//      syncs a clock, nothing posts a chip.
//
// While the answer is in flight the lobby shows the local list with a quiet
// note. That is the honest interim: it is what the game has always shown, and
// the tap that would choose a table is one the player has not made yet.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RoulettePage from './Page';
import { Joining, Lobby } from './Lobby';
import { buildTables, deviceSeed, type Table } from './tables';
import { EMPTY_LOBBY, startSharedLobby, type SharedLobby } from './sharedLobby';
import type { RouletteSharedClock } from './sharedClock';

type Phase = 'lobby' | 'joining' | 'playing';
type Mode = 'asking' | 'local' | 'shared';

export default function RouletteEntry() {
  // Drawn once per device and kept, so the same phone sees the same rooms
  // tomorrow — a lobby whose tables change identity every visit is its own
  // tell — while no two phones ever agree. See tables.ts.
  //
  // LOCAL MODE ONLY. In shared mode the rooms are the server's, the same rooms
  // for every phone, which is what makes a table shared at all.
  const localTables = useMemo(() => buildTables(deviceSeed()), []);
  const [mode, setMode] = useState<Mode>('asking');
  const [shared, setShared] = useState<SharedLobby>(EMPTY_LOBBY);
  const [phase, setPhase] = useState<Phase>('lobby');
  const [table, setTable] = useState<Table | null>(null);
  /** The live clock for the chosen shared table. Built at JOIN, destroyed on leaving. */
  const [clock, setClock] = useState<RouletteSharedClock | null>(null);
  const clockRef = useRef<RouletteSharedClock | null>(null);

  /* ONE question, at the door. The module that answers it is the shared clock's
     own, so a build with the flag off never evaluates a line of Phase E beyond
     this import — and that import is dynamic. */
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const m = await import('./sharedClock');
        const on = await m.sharedTablesOn();
        if (!dead) setMode(on ? 'shared' : 'local');
      } catch {
        // Could not even load the answer: play the game the way it has always
        // played. See `sharedTablesOn`'s own note on why FALSE is the safe way
        // to be wrong in both flag states.
        if (!dead) setMode('local');
      }
    })();
    return () => { dead = true; };
  }, []);

  /* The shared lobby polls only while it is on screen — never behind a table. */
  useEffect(() => {
    if (mode !== 'shared' || phase !== 'lobby') return;
    const stop = startSharedLobby(setShared);
    return stop;
  }, [mode, phase]);

  /* The clock dies with the table, always — including on an unmount that skips
     `back()` (navigating away, the app shell tearing the route down). Without
     this the poller and the Cristian sync would outlive the screen. */
  useEffect(() => () => { clockRef.current?.destroy(); clockRef.current = null; }, []);

  const dropClock = useCallback(() => {
    clockRef.current?.destroy();
    clockRef.current = null;
    setClock(null);
  }, []);

  const [joinError, setJoinError] = useState<string | null>(null);

  const pick = useCallback((t: Table) => {
    setTable(t);
    setJoinError(null);
    setPhase('joining');
    if (mode === 'shared') {
      const serverId = shared.serverIdOf[t.id];
      if (serverId == null) { setPhase('lobby'); setTable(null); return; }
      // Built HERE, not in Page: the join wait is dead time the clock can spend
      // syncing its Cristian offset and landing its first `/state`, so the board
      // opens on the server's truth rather than on a blank first frame.
      void import('./sharedClock').then((m) => {
        if (clockRef.current) return;
        const c = new m.RouletteSharedClock(serverId);
        clockRef.current = c;
        setClock(c);
      }).catch(() => {
        // Nothing was joined and nothing was staked. Back to the lobby with a reason, which
        // is the only honest place to be: mounting the table with no clock would start the
        // LOCAL round loop on a table the server is running, and the first thing it would do
        // is post a slip to an endpoint that no longer exists in this topology.
        setJoinError('Could not open that table — pick another');
        setPhase('lobby');
        setTable(null);
      });
    }
  }, [mode, shared.serverIdOf]);

  const ready = useCallback(() => setPhase('playing'), []);
  /** Settings > Change table. Unmounts Page entirely, which stops the round,
   *  the sound and the emitter — there is nothing left running behind us. */
  const back = useCallback(() => { dropClock(); setPhase('lobby'); setTable(null); }, [dropClock]);
  /* The way OUT. The lobby and the menu used to form a closed loop -- Change
     table led here, and here led only back to a table; the game could not be
     left except by the browser's own chrome. (2026-08-16 audit, C9.) */
  const navigate = useNavigate();
  const exit = useCallback(() => { dropClock(); navigate('/games'); }, [dropClock, navigate]);

  const sharedMode = mode === 'shared';
  const tables = sharedMode ? shared.tables : localTables;
  const note = joinError
    || (mode === 'asking'
      ? 'Finding tables…'
      : sharedMode && !shared.ready
        ? 'Finding tables…'
        : sharedMode && !shared.tables.length
          ? 'No tables are open right now — try again in a moment'
          : null);

  if (phase === 'lobby' || !table) {
    return (
      <Lobby
        tables={tables}
        onPick={pick}
        onExit={exit}
        live={sharedMode ? shared.live : undefined}
        note={note}
      />
    );
  }
  /* THE TABLE IS NOT MOUNTED UNTIL ITS CLOCK EXISTS.
     In shared mode `RoulettePage` with a null `shared` is the LOCAL round loop — a timer that
     closes its own window and posts the whole slip to `/spin`. On a server-run table that is
     the wrong game entirely, so the join screen holds (it has already counted down; the extra
     wait is the dynamic import, which is milliseconds) until the clock is in hand. */
  if (phase === 'joining' || (sharedMode && !clock)) {
    return <Joining table={table} onReady={ready} hist={sharedMode ? (shared.live[table.id]?.hist ?? []) : undefined} />;
  }
  // `key` on the table id: choosing a different room gives a genuinely fresh
  // Page rather than one carrying the last room's bets and history.
  return <RoulettePage key={table.id} table={table} onChangeTable={back} shared={sharedMode ? clock : null} />;
}
